import { libLastWinsReducer, LibLastWinsStoreState } from '@bcraft-frontend-lib/last-wins'
import { AnyAction, applyMiddleware, combineReducers, compose, createStore, Reducer, Store } from 'redux'
import { persistStore } from 'redux-persist'
import { Persistor } from 'redux-persist/es/types'
import thunk from 'redux-thunk'

import { BaseHttpService } from 'services/api/BaseHttpService'

import * as AltenarFeature from 'features/altenar'
import * as AuthFeature from 'features/auth'
import * as BetbyFeature from 'features/betby'
import * as BonusHistoryFeature from 'features/bonusHistory'
import * as BonusSystemFeature from 'features/bonusSystem'
import * as DigitainFeature from 'features/digitain'
import * as JackpotsFeature from 'features/jackpots'
import * as LocaleFeature from 'features/locale'
import * as NotifiesFeature from 'features/notifies'
import * as ProfileFeature from 'features/profile'
import * as PwaFeature from 'features/pwa'
import * as SettingsFeature from 'features/settings'
import * as SidePanelFeature from 'features/sidePanel'
import * as SliderFeature from 'features/slider'
import * as SlotsFeature from 'features/slots'
import * as SoftionFeature from 'features/softion'
import * as UserSettingsFeature from 'features/userSettings'
import * as WheelGameFeature from 'features/wheelGame'

import { IExtra } from '../shared/types/redux'
import { makeUnauthorizedInterceptor } from './interceptors/unauthorizedInterceptor'

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION__?: typeof compose
  }
}
export type IApplicationState = LibLastWinsStoreState & {
  auth: AuthFeature.types.IAuthStatePersisted
  locale: LocaleFeature.types.ILocaleState
  userSettings: UserSettingsFeature.types.IUserSettingsStatePersisted
  settings: SettingsFeature.types.ISettingsState
  profile: ProfileFeature.types.IProfileState
  slots: SlotsFeature.types.ISlotsStatePersisted
  bonusHistory: BonusHistoryFeature.types.IBonusHistoryState
  wheelGame: WheelGameFeature.types.IWheelGameState
  bonusSystem: BonusSystemFeature.types.IBonusSystemState
  softion: SoftionFeature.types.ISoftionState
  jackpots: JackpotsFeature.types.IJackpotsState
  notifies: NotifiesFeature.types.INotifiesStatePersisted
  pwa: PwaFeature.types.IPWAStatePersisted
  sidePanel: SidePanelFeature.types.ISidePanelState
  slider: SliderFeature.types.ISliderStatePersisted
  altenar: AltenarFeature.types.IAltenarState
  digitain: DigitainFeature.types.IDigitainState
  betby: BetbyFeature.types.IBetbyState
}

type ConfigureStore = {
  store: Store<IApplicationState>
  persistor: Persistor
}

export function configureStore(extra: IExtra): ConfigureStore {
  const middleware = thunk.withExtraArgument<IExtra>(extra)

  const composeEnhancers: any =
    window.__REDUX_DEVTOOLS_EXTENSION__ && process.env.NODE_ENV !== 'production'
      ? window.__REDUX_DEVTOOLS_EXTENSION__()
      : (arg: any) => arg

  const reducer: Reducer<IApplicationState, AnyAction> = combineReducers({
    auth: AuthFeature.reducer,
    locale: LocaleFeature.reducer,
    userSettings: UserSettingsFeature.reducer,
    settings: SettingsFeature.reducer,
    profile: ProfileFeature.reducer,
    slots: SlotsFeature.reducer,
    bonusHistory: BonusHistoryFeature.reducer,
    wheelGame: WheelGameFeature.reducer,
    bonusSystem: BonusSystemFeature.reducer,
    softion: SoftionFeature.reducer,
    jackpots: JackpotsFeature.reducer,
    notifies: NotifiesFeature.reducer,
    pwa: PwaFeature.reducer,
    sidePanel: SidePanelFeature.reducer,
    slider: SliderFeature.reducer,
    altenar: AltenarFeature.reducer,
    digitain: DigitainFeature.reducer,
    betby: BetbyFeature.reducer,
    lib: combineReducers({
      lastWins: libLastWinsReducer,
    }),
  })

  const store = createStore(reducer, compose(applyMiddleware(middleware), composeEnhancers))

  const persistor = persistStore(store)

  BaseHttpService.addErrorInterceptor(makeUnauthorizedInterceptor(store))

  BaseHttpService.addTokenInterceptor(store)

  return { store, persistor }
}
